import React from 'react';
import { useApi } from '../hooks/useApi';
import { fmtNum, holdOr, PNL_HOLD } from '../hooks/useApi';
import PortfolioSummary, { cashNotice } from './PortfolioSummary';
import BotLeaderboard from './BotLeaderboard';
import TradeHistory from './TradeHistory';
import RiskPanel from './RiskPanel';
import PersonaComments from './PersonaComments';
import ScreenerResults from './ScreenerResults';
import NewsSection from './NewsSection';
import FundamentalPanel from './FundamentalPanel';
import OpsPanel from './OpsPanel';
import PersonaTab from './PersonaTab';
import ConsensusTab from './ConsensusTab';
import BotDailyPerformance from './BotDailyPerformance';
import SyncFreshnessBadge from './SyncFreshnessBadge';
import QuantPanel from './QuantPanel';

export default function Dashboard({ activeTab }) {
  const { data: summary, loading: sumLoading } = useApi('/api/account/summary');
  const { data: portfolios, loading: portLoading } = useApi('/api/account/portfolios');

  if (activeTab === 'bots') {
    return (
      <div className="space-y-4">
        <BotLeaderboard />
        <PersonaComments />
      </div>
    );
  }

  if (activeTab === 'performance') return <BotDailyPerformance />;

  if (activeTab === 'consensus') return <ConsensusTab />;
  if (activeTab === 'fundamental') return <FundamentalPanel />;
  if (activeTab === 'trades') return <TradeHistory />;
  if (activeTab === 'risk') return <RiskPanel />;
  if (activeTab === 'quant') return <QuantPanel />;
  if (activeTab === 'persona') return <PersonaTab />;

  // Default: dashboard
  return (
    <div className="space-y-4">
      {/* 게이트 상태는 거래 통계·계정 API 장애와 독립적으로 공개한다. */}
      <RealAccountGatePanel />

      {/* 대표님 판단용 최상단 요약 (NEX-1183 2단계) */}
      <ExecutiveSummary summary={summary} />

      {/* 전체 요약 카드 */}
      <SystemSummary data={summary} loading={sumLoading} />

      {/* 포트폴리오 그리드 */}
      <PortfolioSummary portfolios={portfolios} loading={portLoading} />

      {/* 2-column: 리더보드 + 스크리너 (반응형 1열) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BotLeaderboard compact />
        <ScreenerResults />
      </div>

      {/* 뉴스 섹션 (수정 6) */}
      <NewsSection />

      {/* 펀더멘털 패널 (수정 7) */}
      <FundamentalPanel />

      {/* 봇 코멘트 */}
      <PersonaComments limit={5} />

      {/* 운영 패널 (수정 5) */}
      <OpsPanel />
    </div>
  );
}

/**
 * 대표님이 판단에 쓰는 숫자를 첫 화면 맨 위에 (NEX-1183 2단계 항목1).
 *
 * 재기준 이후 통계와 이전 원장 기록을 분리하며 주간 관측 경계를 표시한다.
 */
function ExecutiveSummary({ summary }) {
  const { data: tradeStats, loading: tsLoading } = useApi('/api/trades/stats');
  const { data: leaderboard, loading: lbLoading } = useApi('/api/bots/leaderboard');
  const { data: consensusStats, loading: csLoading } = useApi('/api/consensus/stats');
  const { data: consensusRecent } = useApi('/api/consensus?limit=200');

  const loading = tsLoading || lbLoading || csLoading;
  if (loading || !tradeStats) {
    return <div className="animate-pulse h-28 bg-gray-800 rounded-lg" />;
  }

  const pnl = tradeStats.total_realized_pnl;
  const pnlColor = pnl == null ? 'text-yellow-300' : pnl >= 0 ? 'text-green-400' : 'text-red-400';
  const pnlSign = pnl != null && pnl >= 0 ? '+' : '';

  let winRate = null;
  if (leaderboard && leaderboard.length) {
    const totalTrades = leaderboard.reduce((s, b) => s + (b.closed_trades ?? b.total_trades ?? 0), 0);
    const totalWins = leaderboard.reduce((s, b) => s + (b.winning_trades || 0), 0);
    winRate = totalTrades ? (totalWins / totalTrades * 100) : null;
  }

  let variantBDays = null;
  const variantBCount = consensusStats?.variant_b_count ?? 0;
  if (variantBCount > 0 && consensusRecent) {
    const bDates = consensusRecent
      .filter((c) => c.threshold_variant === 'b' && c.decided_at)
      .map((c) => new Date(c.decided_at.replace(' ', 'T')).getTime());
    if (bDates.length) {
      const earliest = Math.min(...bDates);
      variantBDays = Math.max(0, Math.floor((Date.now() - earliest) / 86400000));
    }
  }

  return (
    <div className="bg-gray-800 rounded-lg p-3 md:p-4 border border-blue-800/60">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-300">대표님 요약</h2>
        <SyncFreshnessBadge lastSyncedAt={summary?.source_observed_at} label="원천 시세 관측" />
        <span className="text-xs text-gray-400">주문 동기화 (체결 시각과 별도)</span>
        <SyncFreshnessBadge lastSyncedAt={tradeStats.source_observed_at} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
        <MetricCard
          label="재기준 이후 누적 실현손익 (모의계좌)"
          value={holdOr(pnl, (v) => `${pnlSign}${fmtNum(v)}`)}
          unit={pnl == null ? '' : '원'}
          valueColor={pnlColor}
          highlight
        />
        <MetricCard
          label="이번 주 손익 (일간 관측 기준)"
          value={summary?.weekly_pnl == null ? '—' : `${summary.weekly_pnl >= 0 ? '+' : ''}${fmtNum(summary.weekly_pnl)}`}
          unit={summary?.weekly_pnl == null ? (summary?.weekly_reason || '관측 대기') : '원'}
          valueColor={summary?.weekly_pnl == null ? 'text-gray-500' : summary.weekly_pnl >= 0 ? 'text-green-400' : 'text-red-400'}
        />
        <MetricCard
          label="승률 (전체 봇 집계)"
          value={winRate == null ? '—' : winRate.toFixed(1)}
          unit={winRate == null ? '(표본 없음)' : '%'}
        />
        <MetricCard
          label="B안 실험"
          value={variantBCount === 0 ? '미시작' : (variantBDays == null ? '집계중' : `D+${variantBDays}`)}
          unit={variantBCount ? `${variantBCount}건` : ''}
        />
      </div>
      {tradeStats.epoch_id && <div className="mt-2 text-xs text-gray-400">
        재기준: {tradeStats.effective_at_utc ? new Date(tradeStats.effective_at_utc).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }) : '시각 확인 중'} (KST)
        {' · '}{tradeStats.legacy_realized_label || '재기준 이전 원장 기록 · 신뢰 불가'}:
        {' '}{tradeStats.legacy_realized_pnl == null ? '산출 보류' : `${fmtNum(tradeStats.legacy_realized_pnl)}원`}
        <div>{tradeStats.realized_pnl_basis}</div>
      </div>}
      {summary?.weekly_period_start_utc && <div className="mt-1 text-xs text-gray-400">
        주간 실제 경계 (KST): {new Date(summary.weekly_period_start_utc).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
        {' ~ '}{new Date(summary.weekly_period_end_utc).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
      </div>}
      <div className="mt-2 text-xs text-gray-500">{summary?.weekly_basis}</div>
    </div>
  );
}

const GATE_LABELS = {
  G1: '최근 완료 4주 실현손익',
  G2: '한국장 주문 재개·원인 판정',
  G3: '단일 −10% 이하 손실 없음',
  G4: '잔고 오탐·통화 혼합',
};

const GATE_REASONS = {
  source_not_defined: '기계 판독 정본이 정해지지 않아 미판정',
  no_active_epoch: '활성 epoch 자료 없음',
  replica_stale: '복제 데이터가 최신 상태가 아님',
  kr_resume_not_observed: '한국장 브로커 체결을 아직 확인하지 못함',
  market_unresolvable: '주문 시장을 확인할 수 없음',
  no_closed_trades: '재기준 이후 청산 거래가 없음',
  insufficient_positive_week_evidence: '완료주 4개의 양수 손익 근거가 부족함',
  missing_krw_conversion: '일부 실현손익의 원화 환산 근거가 없음',
  negative_completed_week: '완료주 중 손실 주가 확인됨',
  missing_realized_pnl_pct: '일부 청산 거래의 손익률 근거가 없음',
  ledger_unavailable: '원장 근거를 읽을 수 없음',
  activation_integrity: 'epoch 무결성 검증에 실패함',
  replica_integrity: '복제 원장 무결성 검증에 실패함',
};

export function RealAccountGatePanel() {
  const { data, loading } = useApi('/api/gates/real-account');
  if (loading) return <div className="mt-3 text-sm text-gray-400">실계좌 전환 관문을 불러오는 중…</div>;
  const gates = data?.gates;
  if (!gates) return <div className="mt-3 text-sm text-gray-400">실계좌 전환 관문: 미제공</div>;
  const statusStyles = {
    PASS: 'border-green-800 bg-green-950/40 text-green-300',
    FAIL: 'border-red-800 bg-red-950/40 text-red-300',
    UNKNOWN: 'border-gray-700 bg-gray-900/60 text-gray-300',
  };
  const statusLabels = { PASS: '통과', FAIL: '불통과', UNKNOWN: '미판정' };
  const countText = (gate) => [
    gate.weeks_observed && `관측 주 ${gate.weeks_observed}`,
    gate.kr_fills_observed != null && `한국장 체결 ${gate.kr_fills_observed}건`,
    gate.closed_trades_observed != null && `청산 ${gate.closed_trades_observed}건`,
    gate.violations_observed != null && `위반 ${gate.violations_observed}건`,
  ].filter(Boolean).join(' · ');
  return (
    <section className="mt-3" aria-label="실계좌 전환 관문">
      <h3 className="mb-2 text-sm font-semibold text-gray-300">실계좌 전환 관문 G1~G4</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {['G1', 'G2', 'G3', 'G4'].map((key) => {
          const gate = gates[key] || { status: 'UNKNOWN', reason: '미제공', as_of_utc: null };
          const status = ['PASS', 'FAIL', 'UNKNOWN'].includes(gate.status) ? gate.status : 'UNKNOWN';
          const asOf = gate.as_of_utc
            ? new Date(gate.as_of_utc).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
            : '시각 미제공';
          return (
            <div key={key} className={`rounded border px-3 py-2 ${statusStyles[status]}`}>
              <div className="flex justify-between gap-2 text-sm font-medium">
                <span>{key} · {GATE_LABELS[key]}</span><span>{statusLabels[status]}</span>
              </div>
              <div className="mt-1 text-xs">{GATE_REASONS[gate.reason] || gate.reason || '근거 미제공'}</div>
              {countText(gate) && <div className="mt-1 text-xs opacity-80">{countText(gate)}</div>}
              <div className="mt-1 text-xs opacity-70">관측: {asOf} KST</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SystemSummary({ data, loading }) {
  if (loading || !data) {
    return <div className="animate-pulse h-24 bg-gray-800 rounded-lg" />;
  }

  const pnlColor = data.total_pnl == null ? 'text-yellow-300' : data.total_pnl >= 0 ? 'text-green-400' : 'text-red-400';
  const pnlSign = data.total_pnl != null && data.total_pnl >= 0 ? '+' : '';
  const gate = data.pnl_gate;
  const held = gate && !gate.pnl_available;
  const signed = (v) => holdOr(v, (n) => `${n >= 0 ? '+' : ''}${fmtNum(n)}`);

  return (
    <div className="space-y-2">
      {held && (
        <div className="bg-yellow-900/40 border border-yellow-700 text-yellow-200 text-sm rounded-lg px-3 py-2">
          {gate.account_label} · 손익·수익률 {gate.label}
        </div>
      )}
      {/* 상단: 총 평가액 + 손익 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
        <MetricCard label="총 평가액 (전체)" value={holdOr(data.total_value, fmtNum)} unit={held ? '' : '원'} highlight notice={cashNotice(data)} />
        <MetricCard label="현금 (KRW)" value={holdOr(data.total_cash, fmtNum)} unit={held ? '' : '원'} />
        <MetricCard label="현금 (USD)" value={held ? PNL_HOLD : fmtNum(data.total_cash_usd || 0)} unit={held ? '' : '$'} notice={cashNotice(data)} />
        <MetricCard
          label={`총 손익 (초기 ${fmtNum(data.total_initial_value ?? data.total_initial_capital)}원 대비)`}
          value={held ? PNL_HOLD : `${pnlSign}${fmtNum(data.total_pnl)}`}
          unit={held ? '' : `(${data.total_pnl_pct == null ? '—' : `${data.total_pnl_pct >= 0 ? '+' : ''}${Number(data.total_pnl_pct).toFixed(2)}%`})`}
          valueColor={held ? 'text-yellow-300' : pnlColor}
        />
      </div>

      {/* 하단: 개인 vs 공동 분리 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
        <MetricCard label="개인 초기자본 (KRW+USD 환산)" value={fmtNum(data.personal_initial_value ?? data.personal_initial)} unit="원" small />
        <MetricCard label="개인 평가액" value={holdOr(data.personal_value, fmtNum)} unit={held ? '' : '원'} small />
        <MetricCard
          label="개인 손익"
          value={signed(data.personal_pnl)}
          unit={held ? '' : `원${data.personal_pnl_pct == null ? '' : ` (${data.personal_pnl_pct >= 0 ? '+' : ''}${Number(data.personal_pnl_pct).toFixed(2)}%)`}`} small
          valueColor={held ? 'text-yellow-300' : (data.personal_pnl||0) >= 0 ? 'text-green-400' : 'text-red-400'}
        />
        <MetricCard label="공동 초기자본 (KRW+USD 환산)" value={fmtNum(data.joint_initial_value ?? data.joint_initial)} unit="원" small />
        <MetricCard label="공동 평가액" value={holdOr(data.joint_value, fmtNum)} unit={held ? '' : '원'} small />
        <MetricCard
          label="공동 손익"
          value={signed(data.joint_pnl)}
          unit={held ? '' : `원${data.joint_pnl_pct == null ? '' : ` (${data.joint_pnl_pct >= 0 ? '+' : ''}${Number(data.joint_pnl_pct).toFixed(2)}%)`}`} small
          valueColor={held ? 'text-yellow-300' : (data.joint_pnl||0) >= 0 ? 'text-green-400' : 'text-red-400'}
        />
      </div>
    </div>
  );
}

function MetricCard({ label, value, unit, highlight, small, valueColor, notice }) {
  return (
    <div className={`rounded-lg p-3 md:p-4 border ${
      highlight ? 'bg-blue-900/30 border-blue-700' : 'bg-gray-800 border-gray-700'
    }`}>
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`${small ? 'text-sm' : 'text-base md:text-lg'} font-semibold ${valueColor || ''}`}>
        {value} <span className="text-xs text-gray-400">{unit}</span>
      </div>
      {notice && <div className="mt-1 text-xs text-yellow-300">{notice}</div>}
    </div>
  );
}
